// VerticalVideoPlayer.js - Full-screen vertical video player with Swiper v11 CDN
import React, { useEffect, useRef, useState, useCallback } from 'react'
import { Helmet } from 'react-helmet'
import { BsChevronLeft } from 'react-icons/bs'
import { FaVolumeXmark, FaVolumeHigh, FaFacebook, FaWhatsapp, FaArrowLeft } from 'react-icons/fa6'
import { FaXTwitter } from 'react-icons/fa6'
import { getCappedVideoQualityId } from './videoCapping'
import 'bitmovin-player/bitmovinplayer-ui.css'
import './vertical-video.scss'
import { trackAutoplayNavigation, trackViewItem } from '../../lib/gtm'
// Replace with your actual Bitmovin license key
const BITMOVIN_LICENSE_KEY = '34a49c54-19ea-45e1-a196-cda7e07caeb3'
const LIKE_DISLIKE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbwU0BlApZbRng4l7xDVHaG5Fb9qQbe1sOEfmHnQETXOwXR9AeC39fkDtwNwprKJcBiJ1g/exec'

const MIN_BITRATE = 200 * 1000
const MAX_BITRATE = 400 * 1000

// Global flag to prevent multiple Swiper instances
let swiperInitializing = false

const VerticalVideoPlayer = ({ videoId, playlist: initialPlaylist, combinedSlides: initialCombinedSlides, currentVideo, onChangeMeta, onClose, adTag, gtmConfig  }) => {
    const [playlist, setPlaylist] = useState(initialPlaylist || [])
    const [combinedSlides, setCombinedSlides] = useState(initialCombinedSlides || [])
    const [isClient, setIsClient] = useState(false)
    const [swiperInstance, setSwiperInstance] = useState(null)
    const globalMuteState = useRef(true) // Single source of truth: true = muted, false = unmuted
    const [, forceUpdate] = useState({}) // Force re-render when needed
    const [shareModalOpen, setShareModalOpen] = useState(false)

    const [likeDislikeData, setLikeDislikeData] = useState({})
    const [currentSlideData, setCurrentSlideData] = useState(null)
    const [swiperLoaded, setSwiperLoaded] = useState(false)
    const [likeDislikeLoaded, setLikeDislikeLoaded] = useState(false)
    const [adPlayStates, setAdPlayStates] = useState({}) // Track play/pause state for ads

    const playerRefs = useRef({}) // Store multiple player instances
    const uiManagerRefs = useRef({}) // Store multiple UI instances
    const swiperRef = useRef(null)
    const combinedSlidesRef = useRef([]) // Store slides in ref to avoid stale closures
    const currentSlideIndexRef = useRef(0) // Track current slide index 
    const hasInitializedRef = useRef(false)

    // Check if we're on client side and load Swiper CDN
    useEffect(() => {
        setIsClient(true)

        // Load Swiper v11 from CDN
        const loadSwiperCDN = () => {
            // Check if Swiper is already loaded
            if (window.Swiper) {
                setSwiperLoaded(true)
                return
            }

            // Load JS
            const script = document.createElement('script')
            script.src = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js'
            script.onload = () => {
                // Wait a bit for Swiper to be fully available
                setTimeout(() => {
                    if (window.Swiper) {
                        setSwiperLoaded(true)
                    } else {
                        console.error('❌ Swiper constructor not available after load')
                    }
                }, 100)
            }
            script.onerror = () => {
                console.error('❌ Failed to load Swiper JS from CDN')
            }
            document.head.appendChild(script)
        }

        loadSwiperCDN()
    }, [])

    // Fetch like/dislike data from Google Apps Script
    const fetchLikeDislikeData = useCallback(async () => {
        try {
            const response = await fetch(`${LIKE_DISLIKE_SCRIPT_URL}?action=get`)
            if (response.ok) {
                const data = await response.json()
                const likeDislikeMap = {}

                data.forEach((item) => {
                    const articleId = item[0]
                    const likes = item[1] || 0
                    const dislikes = item[2] || 0

                    likeDislikeMap[articleId] = {
                        likes,
                        dislikes,
                        userAction: null,
                    }
                })

                setLikeDislikeData(likeDislikeMap)
                setLikeDislikeLoaded(true)
            } else {
                console.error('Failed to fetch like/dislike data:', response.status)
                setLikeDislikeLoaded(true)
            }
        } catch (error) {
            console.error('Error fetching like/dislike data:', error)
            setLikeDislikeLoaded(true)
        }
    }, [])

    // Cleanup function for specific player
    const cleanupPlayer = useCallback((videoIndex) => {
        const uiManager = uiManagerRefs.current[videoIndex]
        const player = playerRefs.current[videoIndex]

        if (uiManager) {
            try {
                uiManager.release()
                delete uiManagerRefs.current[videoIndex]
            } catch (error) {
                console.error(`Error releasing UI for video ${videoIndex}:`, error)
            }
        }

        if (player) {
            try {
                player.destroy()
                delete playerRefs.current[videoIndex]
            } catch (error) {
                console.error(`Error destroying player for video ${videoIndex}:`, error)
            }
        }
    }, [])

    // Cleanup all players
    const cleanupAllPlayers = useCallback(() => {
        Object.keys(playerRefs.current).forEach((index) => {
            cleanupPlayer(parseInt(index))
        })
    }, [cleanupPlayer])

    // Get signed URL
    const getSignedUrl = useCallback(async (url) => {
        return url
    }, [])

    // Setup Bitmovin players for active slides (like PHP sample)
    const setupBitmovin = useCallback(
        async (swiper) => {
            if (!swiper) {
                return
            }

            const slides = combinedSlidesRef.current

            const playersToKeep = new Set()
            const slideIndexes = [swiper.activeIndex - 1, swiper.activeIndex, swiper.activeIndex + 1]

            for (const idx of slideIndexes) {
                if (idx < 0) {
                    continue
                }
                if (idx >= slides.length) {
                    continue
                }

                // For virtual slides, we need to find the actual DOM element
                const slide = swiper.slides.find((s) => Number(s.getAttribute('data-swiper-slide-index')) === idx)
                if (!slide) {
                    continue
                }

                // Get the inner swiper-slide element that contains the actual video data
                const videoSlide = slide.querySelector('.swiper-slide')
                // if (!videoSlide || videoSlide.classList.contains('ad-slide')) {
                //     // Try to display the ad if this is an ad slide
                //     const adDiv = videoSlide?.querySelector('.ad-container > div[id]')

                //     if (adDiv && !adDiv.hasAttribute('data-loaded')) {
                //         if (window.loadAd) {
                //             setTimeout(() => {
                //                 window.loadAd(adDiv.id)
                //             }, 500)
                //         } else {
                //             console.error('❌ window.loadAd function not available')
                //         }
                //     }
                //     continue
                // }

                const videoContainer = videoSlide.querySelector('.video-container')
                if (!videoContainer) {
                    continue
                }

                const videoDiv = videoContainer.querySelector('div[id]')
                if (!videoDiv) {
                    continue
                }

                // Additional safety checks for the video div
                if (!videoDiv.id) {
                    continue
                }

                // Check if the element is properly attached to the DOM
                if (!document.contains(videoDiv)) {
                    continue
                }

                // Ensure the element has dimensions (important for Bitmovin)
                const rect = videoDiv.getBoundingClientRect()
                if (rect.width === 0 || rect.height === 0) {
                    // Set explicit dimensions
                    videoDiv.style.width = '100%'
                    videoDiv.style.height = '100%'
                    videoDiv.style.minHeight = '200px'
                    videoDiv.style.position = 'relative'
                }

                const videoMediaId = videoDiv.id
                playersToKeep.add(videoMediaId)

                // If player already exists, just ensure it's ready
                if (playerRefs.current[videoMediaId]) {
                    playerRefs.current[videoMediaId].seek && playerRefs.current[videoMediaId].seek(0)
                    continue
                }

                // Get video data
                const videoTitle = videoSlide.getAttribute('data-title')
                const videoFile = videoSlide.getAttribute('data-video-file')
                const videoPoster = videoSlide.getAttribute('data-poster')

                if (!videoFile) continue

                try {
                    // Wait a bit to ensure DOM is fully ready
                    await new Promise((resolve) => setTimeout(resolve, 100))

                    // Double-check the element is still valid after the delay
                    if (!document.contains(videoDiv)) {
                        continue
                    }

                    // Initialize per-player variables
                    let availableQualities = {};
                    let minLimit = { bitrate: MIN_BITRATE };
                    let maxLimit = { bitrate: MAX_BITRATE };

                    const videoAdaptationHandler = (e) => {
                        try {
                            // Safety check - ensure availableQualities is populated
                            if (!availableQualities || Object.keys(availableQualities).length === 0) {
                                console.log('availableQualities not ready, using suggested quality');
                                return e.suggested;
                            }

                            const q = availableQualities[e.suggested];

                            if (q) {
                                if (q.bitrate < minLimit.bitrate) {
                                    console.log('bitrate too small, use the lowest quality with bitrate >= minLimit');
                                    return minLimit.quality ? minLimit.quality.id : e.suggested;
                                } else if (q.bitrate > maxLimit.bitrate) {
                                    console.log('bitrate too high, use the highest quality with bitrate <= maxLimit');
                                    return maxLimit.quality ? maxLimit.quality.id : e.suggested;
                                }
                            }

                            console.log('use the automatically selected quality');
                            return e.suggested;
                        } catch (error) {
                            console.error('video adaptation handler error: ', error);
                            return e.suggested; // Fallback to suggested quality
                        }
                    };

                    const updateMinLimit = (limit) => {
                        try {
                            limit = Number(limit);
                            if (!isNaN(limit) && availableQualities && Object.keys(availableQualities).length > 0) {
                                minLimit = { bitrate: limit };
                                const availQ = Object.values(availableQualities).filter(function (elem) {
                                    return elem.bitrate >= minLimit.bitrate;
                                });
                                if (availQ.length < 1) {
                                    console.log('invalid limit, no quality left. Ignore limit');
                                    return;
                                }
                                minLimit.quality = availQ.reduce(function (a, b) {
                                    if (a.bitrate <= b.bitrate) {
                                        return a;
                                    }
                                    return b;
                                });
                                console.log('updated min limit with ' + limit);
                            }
                        } catch (error) {
                          console.error('Error updating min limit:', error);
                        }
                      };

                      const updateMaxLimit = (limit) => {
                          try {
                              limit = Number(limit);
                              if (!isNaN(limit) && availableQualities && Object.keys(availableQualities).length > 0) {
                                  maxLimit = { bitrate: limit };
                                  const availQ = Object.values(availableQualities).filter(elem => elem.bitrate <= maxLimit.bitrate)
                                  if (availQ.length < 1) {
                                      console.log('invalid limit, no quality left. Ignore limit');
                                      return;
                                  }
                                  maxLimit.quality = availQ.reduce((a, b) => {
                                      if (a.bitrate > b.bitrate) {
                                          return a;
                                      }
                                      return b;
                                  });
                                  console.log('updated max limit with ' + limit);
                              }
                          } catch (error) {
                            console.error('Error updating max limit:', error);
                          }
                      };

                    const { Player } = await import('bitmovin-player')
                    const { UIFactory } = await import('bitmovin-player-ui')

                    const playerConfig = {
                        key: BITMOVIN_LICENSE_KEY,
                        playback: {
                            muted: true,
                            autoplay: false,
                        },
                        advertising: {
                            adBreaks: [
                                {
                                    tag: {
                                        type: 'vmap',
                                        url: adTag,
                                    },
                                },
                            ],
                        },
                        // adaptation: {
                        //     startupBitrate: 300000,
                        //     desktop: {
                        //         onVideoAdaptation: videoAdaptationHandler,
                        //     },
                        //     mobile: {
                        //         onVideoAdaptation: videoAdaptationHandler,
                        //     }
                        // },
                        ui: false,
                        style: {
                            width: '100%',
                            height: '100%',
                        },
                    }

                    // Ensure the video div has proper styling before player creation
                    videoDiv.style.width = '100%'
                    videoDiv.style.height = '100%'
                    videoDiv.style.position = 'relative'
                    videoDiv.style.backgroundColor = '#000'

                    const playerInstance = new Player(videoDiv, playerConfig)
                    playerRefs.current[videoMediaId] = playerInstance

                    const uiInstance = UIFactory.buildDefaultUI(playerInstance)
                    uiManagerRefs.current[videoMediaId] = uiInstance

                    const signedUrl = await getSignedUrl(videoFile)

                    // Setup source
                    const sourceConfig = {
                        title: videoTitle,
                        poster: videoPoster,
                        hls: signedUrl,
                    }

                    playerInstance.on('ready', () => {
                        // Style adjustments
                        const container = videoDiv
                        if (container) {
                            container.style.width = '100%'
                            container.style.height = '100%'
                            container.style.position = 'relative'

                            // Mobile-specific adjustments
                            if (window.innerWidth <= 768) {
                                container.classList.add('mobile-player')
                                const videoElement = container.querySelector('video')
                                if (videoElement) {
                                    videoElement.style.width = '100%'
                                    videoElement.style.height = '100%'
                                    videoElement.style.objectFit = 'contain'
                                }
                            }
                        }

                        // Auto-play if this is the active slide
                        if (idx === swiper.activeIndex) {
                            // playerInstance.play().catch((e) => console.log('Autoplay prevented:', e))
                        }
                    })

                    playerInstance.on('error', (error) => {
                        console.error(`Player error for ${videoTitle}:`, error)
                    })

                    await playerInstance.load(sourceConfig).then(() => {

                        // console.log('[Ad Debug] Player loaded, advertising config:', playerInstance.getConfig()?.advertising)
                        availableQualities = {}
                        const qualities = playerInstance.getAvailableVideoQualities()
                        console.log('Available quality: ', qualities)
                        qualities.forEach(function (q) {
                            availableQualities[q.id] = q;
                        });

                        updateMinLimit(MIN_BITRATE)
                        updateMaxLimit(MAX_BITRATE)

                        // Listen to VideoAdaptation event
                        playerInstance.on(playerInstance.exports.PlayerEvent.VideoAdaptation, function (event) {
                            console.log('Current quality in desktop: ', event);
                        });

                        if (!globalMuteState.current) {
                            playerInstance.unmute && playerInstance.unmute()
                        }
                    })

                    // Ad event listeners
                    playerInstance.on('adstarted', () => {
                        // Show ad play/pause button and set initial state as playing
                        setAdPlayStates((prev) => ({
                            ...prev,
                            [videoMediaId]: { isAdPlaying: true, showAdControls: true },
                        }))
                    })

                    playerInstance.on('adfinished', () => {
                        // Hide ad play/pause button
                        setAdPlayStates((prev) => ({
                            ...prev,
                            [videoMediaId]: { isAdPlaying: false, showAdControls: false },
                        }))
                    })

                    // Additional events to ensure button is hidden when main content plays
                    playerInstance.on('adskipped', () => {
                        // Hide ad play/pause button
                        setAdPlayStates((prev) => ({
                            ...prev,
                            [videoMediaId]: { isAdPlaying: false, showAdControls: false },
                        }))
                    })

                    playerInstance.on('aderror', () => {
                        // Hide ad play/pause button
                        setAdPlayStates((prev) => ({
                            ...prev,
                            [videoMediaId]: { isAdPlaying: false, showAdControls: false },
                        }))
                    })

                    // Track ad play/pause state - multiple event types for better compatibility
                    playerInstance.on('adpaused', () => {
                        setAdPlayStates((prev) => ({
                            ...prev,
                            [videoMediaId]: { ...prev[videoMediaId], isAdPlaying: false },
                        }))
                    })

                    playerInstance.on('adresumed', () => {
                        setAdPlayStates((prev) => ({
                            ...prev,
                            [videoMediaId]: { ...prev[videoMediaId], isAdPlaying: true },
                        }))
                    })

                    // Additional event listeners for better compatibility
                    playerInstance.on('pause', () => {
                        // Small delay to ensure we capture the correct state
                        setTimeout(() => {
                            // Check if we're currently in an ad
                            setAdPlayStates((prev) => {
                                const currentState = prev[videoMediaId]
                                if (currentState?.showAdControls) {
                                    return {
                                        ...prev,
                                        [videoMediaId]: { ...prev[videoMediaId], isAdPlaying: false },
                                    }
                                }
                                return prev
                            })
                        }, 50)
                    })

                    const videoDuration = parseInt(videoSlide.getAttribute('data-duration') || '0', 10);

                    playerInstance.on('play', () => {
                        //Track Play Video Event
                        const videoData = { title: videoTitle, duration: videoDuration };

                        // console.log('[view_item] about to push', {
                        //     videoData,
                        //     idx,
                        //     listId: gtmConfig?.listId,
                        //     listName: gtmConfig?.listName,
                        //     category: gtmConfig?.category,
                        //     category2: gtmConfig?.category2,
                        // });
                        trackViewItem(videoData, idx, gtmConfig?.listId, gtmConfig?.listName, gtmConfig?.category, gtmConfig?.category2);

                        // Small delay to ensure we capture the correct state
                        setTimeout(() => {
                            // Check if we're currently in an ad
                            setAdPlayStates((prev) => {
                                const currentState = prev[videoMediaId]
                                if (currentState?.showAdControls) {
                                    return {
                                        ...prev,
                                        [videoMediaId]: { ...prev[videoMediaId], isAdPlaying: true },
                                    }
                                }
                                return prev
                            })
                        }, 50)
                    })
                } catch (error) {
                    console.error(`❌ Error initializing player for ${videoTitle}:`, error)
                    console.error(`Error details:`, {
                        message: error.message,
                        stack: error.stack,
                        videoDiv: videoDiv,
                        videoDivId: videoDiv?.id,
                        videoDivInDOM: videoDiv ? document.contains(videoDiv) : false,
                    })
                }
            }

            // Cleanup players not in the keep list
            Object.keys(playerRefs.current).forEach((id) => {
                if (!playersToKeep.has(id)) {
                    try {
                        if (playerRefs.current[id]) {
                            playerRefs.current[id].destroy && playerRefs.current[id].destroy()
                            delete playerRefs.current[id]
                        }
                        if (uiManagerRefs.current[id]) {
                            uiManagerRefs.current[id].release && uiManagerRefs.current[id].release()
                            delete uiManagerRefs.current[id]
                        }
                    } catch (e) {
                        console.error('Error cleaning up player:', e)
                    }
                }
            })
        },
        [getSignedUrl]
    )

    // Play video on active slide
    const playVideo = useCallback((swiper) => {
        if (!swiper) return

        const activeIdx = swiper.activeIndex
        const activeSlide = swiper.slides.find((s) => Number(s.getAttribute('data-swiper-slide-index')) === activeIdx)

        if (!activeSlide) return

        // Get the inner swiper-slide element that contains the actual video data
        const videoSlide = activeSlide.querySelector('.swiper-slide')
        if (!videoSlide || videoSlide.classList.contains('ad-slide')) return

        const videoContainer = videoSlide.querySelector('.video-container')
        if (!videoContainer) return

        const videoDiv = videoContainer.querySelector('div[id]')
        if (!videoDiv) return

        const videoMediaId = videoDiv.id
        if (playerRefs.current[videoMediaId]) {
            playerRefs.current[videoMediaId].play().catch((e) => console.log('Play error:', e))
        }
    }, [])

    // Pause previous video
    const pauseVideo = useCallback((swiper) => {
        if (!swiper || typeof swiper.previousIndex !== 'number') return

        const prevIdx = swiper.previousIndex
        const prevSlide = swiper.slides.find((s) => Number(s.getAttribute('data-swiper-slide-index')) === prevIdx)

        if (!prevSlide) return

        // Get the inner swiper-slide element that contains the actual video data
        const videoSlide = prevSlide.querySelector('.swiper-slide')
        if (!videoSlide || videoSlide.classList.contains('ad-slide')) return

        const videoContainer = videoSlide.querySelector('.video-container')
        if (!videoContainer) return

        const videoDiv = videoContainer.querySelector('div[id]')
        if (!videoDiv) return

        const videoMediaId = videoDiv.id
        if (playerRefs.current[videoMediaId]) {
            playerRefs.current[videoMediaId].pause && playerRefs.current[videoMediaId].pause()
        }
    }, [])

    // Handle share
    const handleShare = useCallback(() => {
        setShareModalOpen(true)
    }, [])

    const shareOnSocial = useCallback(
        (platform) => {
            if (!isClient) return
            const shareUrl = window.location.href
            const shareText = currentSlideData?.title || ''

            let url = ''
            switch (platform) {
                case 'facebook':
                    url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
                    break
                case 'twitter':
                    url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`
                    break
                case 'whatsapp':
                    url = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`
                    break
                default:
                    return
            }

            window.open(url, '_blank')
            setShareModalOpen(false)
        },
        [isClient, currentSlideData]
    )

    const copyToClipboard = useCallback(() => {
        if (!isClient) return
        const shareUrl = window.location.href
        navigator.clipboard.writeText(shareUrl).then(() => {
            alert('Link copied to clipboard!')
        })
    }, [isClient])

    // Toggle global mute/unmute - Single source of truth approach
    const toggleGlobalMute = useCallback(() => {
        // Flip the global mute state
        globalMuteState.current = !globalMuteState.current
        const isMuted = globalMuteState.current

        // Apply the new state to ALL existing players immediately
        Object.values(playerRefs.current).forEach((player) => {
            try {
                if (isMuted) {
                    player.mute && player.mute()
                } else {
                    player.unmute && player.unmute()
                }
            } catch (error) {
                console.error('Error applying global mute state to player:', error)
            }
        })

        // Force re-render to update the button icon
        forceUpdate({})
    }, [forceUpdate])

    // Handle like/dislike
    const handleLikeDislike = useCallback(
        async (action, videoId) => {
            // Get current data
            const current = likeDislikeData[videoId] || { likes: 0, dislikes: 0, userAction: null }
            const alreadyInteracted = current.userAction !== null

            // Calculate new data
            const newData = { ...current }

            if (current.userAction === action) {
                // User clicked the same action - do nothing
                return // Exit early, no changes
            } else if (!alreadyInteracted) {
                // First interaction
                if (action === 'like') {
                    newData.likes += 1
                } else {
                    newData.dislikes += 1
                }
                newData.userAction = action
            } else {
                // Changing from one to another (switching)

                if (action === 'like') {
                    // Switching from dislike to like
                    newData.likes += 1
                    newData.dislikes = Math.max(0, newData.dislikes - 1)
                } else {
                    // Switching from like to dislike
                    newData.dislikes += 1
                    newData.likes = Math.max(0, newData.likes - 1)
                }
                newData.userAction = action
            }

            // 1. UPDATE UI IMMEDIATELY for better UX
            setLikeDislikeData((prev) => ({
                ...prev,
                [videoId]: newData,
            }))

            // 2. Update the current slide's UI immediately
            const currentSlide = document.querySelector('.swiper-slide-visible .swiper-slide')
            if (currentSlide && currentSlide.getAttribute('data-video-id') === videoId) {
                const likeCountElement = currentSlide.querySelector('.like-count')
                const dislikeCountElement = currentSlide.querySelector('.dislike-count')
                const likeBtnElement = currentSlide.querySelector('.like-btn')
                const dislikeBtnElement = currentSlide.querySelector('.dislike-btn')

                // Update counts immediately
                if (likeCountElement) likeCountElement.textContent = newData.likes
                if (dislikeCountElement) dislikeCountElement.textContent = newData.dislikes

                // Update visual state immediately
                if (likeBtnElement && dislikeBtnElement) {
                    // Always clear both buttons first
                    likeBtnElement.classList.remove('checked')
                    dislikeBtnElement.classList.remove('checked')

                    // Clear radio inputs
                    const likeRadio = likeBtnElement.querySelector('input[type="radio"]')
                    const dislikeRadio = dislikeBtnElement.querySelector('input[type="radio"]')
                    if (likeRadio) likeRadio.checked = false
                    if (dislikeRadio) dislikeRadio.checked = false

                    // Set the active state based on newData.userAction
                    if (newData.userAction === 'like') {
                        likeBtnElement.classList.add('checked')
                        if (likeRadio) likeRadio.checked = true
                    } else if (newData.userAction === 'dislike') {
                        dislikeBtnElement.classList.add('checked')
                        if (dislikeRadio) dislikeRadio.checked = true
                    }
                }
            }

            // 3. THEN send to API in the background (non-blocking)
            const sendToAPI = async () => {
                try {
                    const response = await fetch(LIKE_DISLIKE_SCRIPT_URL, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded',
                        },
                        body: new URLSearchParams({
                            action: action, // Always send the original action (like/dislike)
                            articleID: videoId,
                            alreadyInteracted: alreadyInteracted.toString(),
                        }),
                    })

                    if (response.ok) {
                        const result = await response.text()
                    } else {
                        console.error('❌ Failed to update like/dislike in API:', response.status)
                        // TODO: Could implement retry logic or rollback UI changes here
                    }
                } catch (error) {
                    console.error('❌ Error updating like/dislike in API:', error)
                }
            }

            // Call API in background without awaiting
            sendToAPI()
        },
        [likeDislikeData]
    )

    // Update meta information based on active slide
    const updateMeta = useCallback(() => {
        const activeSlide = document.querySelector('.swiper-slide-visible')
        if (!activeSlide) return

        const videoSlide = activeSlide.querySelector('.swiper-slide')
        if (!videoSlide || videoSlide.classList.contains('ad-slide')) return

        const videoID = videoSlide.getAttribute('data-video-id')
        const videoTitle = videoSlide.getAttribute('data-title')
        const videoDescription = videoSlide.getAttribute('data-description')
        const videoPoster = videoSlide.getAttribute('data-poster')

        if (!videoID || !videoTitle) return

        // Get the current path, remove the last segment, and add the new video ID
        const pathSegments = window.location.pathname.split('/').filter((segment) => segment)
        if (pathSegments.length > 1) {
            pathSegments[pathSegments.length - 1] = videoID
        } else {
            pathSegments.push(videoID)
        }

        const newUrl = `${window.location.origin}/${pathSegments.join('/')}`
        // history.replaceState({ videoId: videoID }, '', newUrl)

        const pageTitle = videoTitle
        const pageDescription = videoDescription || `Tonton video menarik ini di BULETINTV3!`
        const imageUrl = videoPoster

        // Update meta state - this will automatically update the Head component
        onChangeMeta({
            title: pageTitle,
            description: pageDescription,
            url: newUrl,
            image: imageUrl,
            type: 'video.other',
        })

        // Update current slide data for other components
        setCurrentSlideData({
            videoId: videoID,
            title: videoTitle,
            description: pageDescription,
            image: videoPoster,
            type: 'video',
        })
    }, [])

    // Setup like/dislike for active slide
    const setupActiveSliceLikeDislike = useCallback(
        (swiper) => {
            // Get fresh data from current state
            const currentLikeDislikeData = likeDislikeData

            if (!swiper || !likeDislikeLoaded) {
                return
            }

            const activeSlide = swiper.slides.find((s) => Number(s.getAttribute('data-swiper-slide-index')) === swiper.activeIndex)
            if (!activeSlide) {
                return
            }

            const videoSlide = activeSlide.querySelector('.swiper-slide')
            if (!videoSlide || videoSlide.classList.contains('ad-slide')) {
                return
            }

            const videoID = videoSlide.getAttribute('data-video-id')
            if (!videoID) {
                return
            }

            // Update like/dislike counts from stored data
            const data = currentLikeDislikeData[videoID] || { likes: 0, dislikes: 0, userAction: null }

            const likeCountElement = videoSlide.querySelector('.like-count')
            const dislikeCountElement = videoSlide.querySelector('.dislike-count')
            const likeBtnElement = videoSlide.querySelector('.like-btn')
            const dislikeBtnElement = videoSlide.querySelector('.dislike-btn')

            if (likeCountElement) {
                likeCountElement.textContent = data.likes
            }
            if (dislikeCountElement) {
                dislikeCountElement.textContent = data.dislikes
            }

            // Update visual state based on user action
            if (likeBtnElement && dislikeBtnElement) {
                likeBtnElement.classList.remove('checked')
                dislikeBtnElement.classList.remove('checked')

                if (data.userAction === 'like') {
                    likeBtnElement.classList.add('checked')
                    const radioInput = likeBtnElement.querySelector('input[type="radio"]')
                    if (radioInput) radioInput.checked = true
                } else if (data.userAction === 'dislike') {
                    dislikeBtnElement.classList.add('checked')
                    const radioInput = dislikeBtnElement.querySelector('input[type="radio"]')
                    if (radioInput) radioInput.checked = true
                }
            }

            // Remove previous event listeners and add new ones
            const likeBtn = videoSlide.querySelector('.like-btn')
            const dislikeBtn = videoSlide.querySelector('.dislike-btn')
            const shareBtn = videoSlide.querySelector('.social-share-btn')

            if (likeBtn) {
                const newLikeBtn = likeBtn.cloneNode(true)
                likeBtn.parentNode.replaceChild(newLikeBtn, likeBtn)

                newLikeBtn.addEventListener('click', (e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleLikeDislike('like', videoID)
                })
            }

            if (dislikeBtn) {
                const newDislikeBtn = dislikeBtn.cloneNode(true)
                dislikeBtn.parentNode.replaceChild(newDislikeBtn, dislikeBtn)

                newDislikeBtn.addEventListener('click', (e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleLikeDislike('dislike', videoID)
                })
            }

            if (shareBtn) {
                const newShareBtn = shareBtn.cloneNode(true)
                shareBtn.parentNode.replaceChild(newShareBtn, shareBtn)

                newShareBtn.addEventListener('click', (e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleShare()
                })
            }
        },
        [likeDislikeData, likeDislikeLoaded, handleLikeDislike, handleShare]
    )

    // Initialize with server-side data when component mounts
    useEffect(() => {
        if (!isClient) return

        // Initialize meta state with current URL
        onChangeMeta((prev) => ({
            ...prev,
            url: window.location.href,
        }))

        if (initialPlaylist && initialCombinedSlides) {
            setPlaylist(initialPlaylist)
            setCombinedSlides(initialCombinedSlides)
            combinedSlidesRef.current = initialCombinedSlides
        }
    }, [isClient, initialPlaylist, initialCombinedSlides, onChangeMeta])

    // Fetch like/dislike data when component mounts
    useEffect(() => {
        if (!isClient) return
        fetchLikeDislikeData()
    }, [isClient, fetchLikeDislikeData])

    // Update meta when currentVideo prop changes
    useEffect(() => {
        if (currentVideo && isClient) {
            const pageTitle = currentVideo.title
            const pageDescription = currentVideo.description || 'Tonton video menarik ini di BULETINTV3!'
            const imageUrl = currentVideo.image

            onChangeMeta({
                title: pageTitle,
                description: pageDescription,
                url: window.location.href,
                image: imageUrl,
                type: 'video.other',
            })

            // Update document title
            document.title = pageTitle
        }
    }, [currentVideo, isClient, onChangeMeta])

    // Setup like/dislike when data loads or slide changes
    useEffect(() => {
        if (!isClient || !likeDislikeLoaded || !swiperInstance) {
            return
        }

        setupActiveSliceLikeDislike(swiperInstance)
    }, [isClient, likeDislikeLoaded, swiperInstance, setupActiveSliceLikeDislike, likeDislikeData])

    // Watch for slide changes and update like/dislike accordingly
    useEffect(() => {
        if (!swiperInstance || !likeDislikeLoaded || Object.keys(likeDislikeData).length === 0) return

        const handleSlideChange = () => {
            setupActiveSliceLikeDislike(swiperInstance)
        }

        // Add event listener for slide changes
        swiperInstance.on('slideChangeTransitionEnd', handleSlideChange)

        // Cleanup
        return () => {
            if (swiperInstance && swiperInstance.off) {
                swiperInstance.off('slideChangeTransitionEnd', handleSlideChange)
            }
        }
    }, [swiperInstance, likeDislikeLoaded, likeDislikeData, setupActiveSliceLikeDislike])

    // Handle ad play/pause button visibility and events
    useEffect(() => {
        if (!swiperInstance) return

        // Update all ad play/pause buttons based on current state
        Object.keys(adPlayStates).forEach((videoMediaId) => {
            const adState = adPlayStates[videoMediaId]
            const button = document.querySelector(`[data-video-media-id="${videoMediaId}"]`)

            if (button) {
                // Show/hide button based on ad state
                const shouldShow = adState.showAdControls
                button.style.display = shouldShow ? 'flex' : 'none'

                // Update icon based on play state
                const icon = button.querySelector('i')
                if (icon) {
                    const newIconClass = adState.isAdPlaying ? 'fa-solid fa-pause' : 'fa-solid fa-play'
                    if (icon.className !== newIconClass) {
                        icon.className = newIconClass
                    }
                }

                // Only add event listener if it doesn't already exist
                if (!button.hasAttribute('data-listener-added')) {
                    button.setAttribute('data-listener-added', 'true')
                    button.addEventListener('click', (e) => {
                        e.preventDefault()
                        e.stopPropagation()

                        // Call toggle function directly
                        const player = playerRefs.current[videoMediaId]
                        if (!player) return

                        setAdPlayStates((prev) => {
                            const currentState = prev[videoMediaId]
                            if (!currentState || !currentState.showAdControls) return prev

                            try {
                                if (currentState.isAdPlaying) {
                                    player.pause()
                                    return {
                                        ...prev,
                                        [videoMediaId]: { ...prev[videoMediaId], isAdPlaying: false },
                                    }
                                } else {
                                    player.play()
                                    return {
                                        ...prev,
                                        [videoMediaId]: { ...prev[videoMediaId], isAdPlaying: true },
                                    }
                                }
                            } catch (error) {
                                console.error('Error toggling ad play/pause:', error)
                                return prev
                            }
                        })
                    })
                }
            }
        })
    }, [adPlayStates, swiperInstance])

    // Handle page visibility changes (when user clicks ad and navigates to new tab)
    useEffect(() => {
        if (!isClient) return

        const handleVisibilityChange = () => {
            // When page becomes hidden (user navigated away), update all ad states to paused
            if (document.hidden) {
                setAdPlayStates((prev) => {
                    const updated = { ...prev }
                    let hasChanges = false

                    Object.keys(updated).forEach((videoMediaId) => {
                        if (updated[videoMediaId]?.showAdControls && updated[videoMediaId]?.isAdPlaying) {
                            updated[videoMediaId] = { ...updated[videoMediaId], isAdPlaying: false }
                            hasChanges = true
                        }
                    })

                    return hasChanges ? updated : prev
                })
            } else {
                // When page becomes visible again, check actual player states
                Object.keys(adPlayStates).forEach((videoMediaId) => {
                    const adState = adPlayStates[videoMediaId]
                    if (adState?.showAdControls) {
                        const player = playerRefs.current[videoMediaId]
                        if (player) {
                            try {
                                const isPaused = player.isPaused && player.isPaused()

                                setAdPlayStates((prev) => ({
                                    ...prev,
                                    [videoMediaId]: { ...prev[videoMediaId], isAdPlaying: !isPaused },
                                }))
                            } catch (error) {
                                console.error('Error checking player state on visibility change:', error)
                            }
                        }
                    }
                })
            }
        }

        const handleWindowBlur = () => {
            // Similar logic to visibility change
            setAdPlayStates((prev) => {
                const updated = { ...prev }
                let hasChanges = false

                Object.keys(updated).forEach((videoMediaId) => {
                    if (updated[videoMediaId]?.showAdControls && updated[videoMediaId]?.isAdPlaying) {
                        updated[videoMediaId] = { ...updated[videoMediaId], isAdPlaying: false }
                        hasChanges = true
                    }
                })

                return hasChanges ? updated : prev
            })
        }

        const handleWindowFocus = () => {
            // Check actual player states when window regains focus
            setTimeout(() => {
                Object.keys(adPlayStates).forEach((videoMediaId) => {
                    const adState = adPlayStates[videoMediaId]
                    if (adState?.showAdControls) {
                        const player = playerRefs.current[videoMediaId]
                        if (player) {
                            try {
                                const isPaused = player.isPaused && player.isPaused()

                                setAdPlayStates((prev) => ({
                                    ...prev,
                                    [videoMediaId]: { ...prev[videoMediaId], isAdPlaying: !isPaused },
                                }))
                            } catch (error) {
                                console.error('Error checking player state on window focus:', error)
                            }
                        }
                    }
                })
            }, 100) // Small delay to ensure player state is updated
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)
        window.addEventListener('blur', handleWindowBlur)
        window.addEventListener('focus', handleWindowFocus)

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange)
            window.removeEventListener('blur', handleWindowBlur)
            window.removeEventListener('focus', handleWindowFocus)
        }
    }, [isClient, adPlayStates])

    // Periodic state sync to ensure button state matches player state
    useEffect(() => {
        if (!isClient) return

        const syncAdStates = () => {
            Object.keys(adPlayStates).forEach((videoMediaId) => {
                const adState = adPlayStates[videoMediaId]
                if (adState?.showAdControls) {
                    const player = playerRefs.current[videoMediaId]
                    if (player) {
                        try {
                            const isPaused = player.isPaused && player.isPaused()
                            const shouldBePlaying = !isPaused

                            // Only update if there's a mismatch
                            if (adState.isAdPlaying !== shouldBePlaying) {
                                setAdPlayStates((prev) => ({
                                    ...prev,
                                    [videoMediaId]: { ...prev[videoMediaId], isAdPlaying: shouldBePlaying },
                                }))
                            }
                        } catch (error) {
                            // Ignore errors during state sync
                        }
                    }
                }
            })
        }

        // Sync every 2 seconds when there are active ads
        const hasActiveAds = Object.values(adPlayStates).some((state) => state?.showAdControls)
        let interval = null

        if (hasActiveAds) {
            interval = setInterval(syncAdStates, 2000)
        }

        return () => {
            if (interval) {
                clearInterval(interval)
            }
        }
    }, [isClient, adPlayStates])

    // Initialize Swiper when both Swiper is loaded and slides are ready
    useEffect(() => {
        if (!swiperLoaded || !combinedSlides.length || !isClient) return

        const initSwiper = () => {
            // Prevent concurrent initializations
            if (swiperInitializing) {
                return
            }

            // Check if Swiper is already initialized on the DOM element
            const swiperContainer = document.querySelector('.mySwiper')
            if (!swiperContainer) {
                return
            }

            // Check if this element already has a Swiper instance
            if (swiperContainer.swiper) {
                swiperRef.current = swiperContainer.swiper
                setSwiperInstance(swiperContainer.swiper)
                return
            }

            // Double check our ref
            if (swiperRef.current) {
                return
            }

            swiperInitializing = true

            // Double check Swiper is available
            if (!window.Swiper) {
                console.error('❌ Swiper not available on window object')
                swiperInitializing = false
                return
            }
            // Create virtual slides HTML (like in PHP sample)
            const virtualSlides = combinedSlides.map((slide, index) => {
                if (slide.type === 'video') {
                    const videoId = `bitmovin-${slide.index}`
                    return `
                        <div class="swiper-slide" data-id="${videoId}" data-video-id="${slide.mediaid}" data-title="${slide.title}" data-video-file="${slide.hlsSource}" data-poster="${slide.image}" data-duration="${slide.duration || 0}">
                            <div class="video-wrapper">
                                <div class="video-container">
                                    <p class="video-title">${slide.title}</p>
                                    <div id="${videoId}"></div>
                                    <button class="ad-play-pause-btn" data-video-media-id="${videoId}" style="display: none;">
                                        <i class="fa-solid fa-play" style="font-size: 40px;"></i>
                                    </button>
                                    <div class="actions">
                                        <label class="btn like-btn" data-video="${slide.mediaid}">
                                            <input type="radio" name="${slide.mediaid}" value="like">
                                            <span class="icon like-icon"><i class="fa-solid fa-thumbs-up"></i></span>
                                            <span class="like-count">0</span>
                                        </label>
                                        <label class="btn dislike-btn" data-video="${slide.mediaid}">
                                            <input type="radio" name="${slide.mediaid}" value="dislike">
                                            <span class="icon dislike-icon"><i class="fa-solid fa-thumbs-down"></i></span>
                                            <span class="dislike-count">0</span>
                                        </label>
                                        <button class="btn social-share-btn" data-video="${slide.mediaid}">
                                            <span class="icon share-icon"><i class="fa-solid fa-share"></i></span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    `
                } else {
                    return `
                        <div class="swiper-slide quick-container ad-slide">
                            <div class="video-wrapper">
                                <div class="ad-container">
                                    <p class="text-white">IKLAN</p>
                                    <div id='${slide.adId}' style='min-width: 300px; min-height: 250px;'></div>
                                </div>
                            </div>
                        </div>
                    `
                }
            })

            // Find initial index based on videoId
            let initialIndex = 0
            if (videoId) {
                const foundIndex = combinedSlides.findIndex((slide) => slide.type === 'video' && (slide.mediaid === videoId || slide.mediaid === parseInt(videoId)))
                if (foundIndex !== -1) {
                    initialIndex = foundIndex
                }
            }

            // Clear the swiper wrapper first
            const swiperWrapper = document.querySelector('.swiper-wrapper')
            if (swiperWrapper) {
                swiperWrapper.innerHTML = ''
            }

            // Initialize Swiper with exact same config as PHP sample
            const swiper = new window.Swiper('.mySwiper', {
                direction: 'vertical',
                threshold: 20,
                longSwipes: false,
                preventInteractionOnTransition: true,
                touchStartPreventDefault: false,
                mousewheel: {
                    forceToAxis: true,
                },
                keyboard: {
                    enabled: true,
                },
                virtual: {
                    slides: virtualSlides,
                    cache: false,
                },
                loop: false,
                speed: 600,
                navigation: {
                    nextEl: '.swiper-button-next',
                    prevEl: '.swiper-button-prev',
                },
                initialSlide: initialIndex,
                on: {
                    init: (swiper) => {
                        if (initialIndex > 0) {
                            swiper.slideTo(initialIndex, 0)
                        } else {
                            updateMeta()
                            setupBitmovin(swiper)

                            // Check if initial slide is an ad slide
                            const activeSlide = swiper.slides.find((s) => Number(s.getAttribute('data-swiper-slide-index')) === 0)
                            if (activeSlide) {
                                const videoSlide = activeSlide.querySelector('.swiper-slide')
                                if (videoSlide && videoSlide.classList.contains('ad-slide')) {
                                    const adDiv = videoSlide.querySelector('.ad-container > div[id]')
                                    if (adDiv && !adDiv.hasAttribute('data-loaded')) {
                                        setTimeout(() => {
                                            if (window.loadAd) {
                                                window.loadAd(adDiv.id)
                                            }
                                        }, 1000) // Longer delay for initial load
                                    }
                                }
                            }
                        }

                        setTimeout(() => {
                            hasInitializedRef.current = true
                        }, 700)

                        // Note: like/dislike setup moved to useEffect hooks to avoid closure issues
                    },
                    slideChangeTransitionEnd: (swiper) => {
                        updateMeta()
                        setupBitmovin(swiper)
                        // playVideo(swiper)
                        pauseVideo(swiper)

                        // Initialize ad if current slide is an ad slide
                        const activeSlide = swiper.slides.find((s) => Number(s.getAttribute('data-swiper-slide-index')) === swiper.activeIndex)
                        if (activeSlide) {
                            const videoSlide = activeSlide.querySelector('.swiper-slide')
                            if (videoSlide && videoSlide.classList.contains('ad-slide')) {
                                const adDiv = videoSlide.querySelector('.ad-container > div[id]')
                                if (adDiv && !adDiv.hasAttribute('data-loaded')) {
                                    setTimeout(() => {
                                        if (window.loadAd) {
                                            window.loadAd(adDiv.id)
                                        }
                                    }, 500)
                                }
                            }
                        }

                        if (hasInitializedRef.current) {
                            const activeIdx = swiper.activeIndex
                            const activeSlideData = combinedSlidesRef.current[activeIdx]
                            if (activeSlideData && activeSlideData.type === 'video') {
                                // console.log('[GTM] 👆 Desktop navigation — firing select_item', { title: activeSlideData.title, activeIdx })
                                trackAutoplayNavigation(
                                    { title: activeSlideData.title, duration: activeSlideData.duration || 0 },
                                    activeIdx,
                                    gtmConfig?.listId,
                                    gtmConfig?.listName,
                                    gtmConfig?.category,
                                    gtmConfig?.category2
                                )
                            }
                        }

                        // Update current slide index ref to trigger useEffect
                        currentSlideIndexRef.current = swiper.activeIndex

                        //Todo: trackSwipe(swiper)
                    },
                },
            })

            setSwiperInstance(swiper)
            swiperRef.current = swiper
            swiperInitializing = false
        }

        // Small delay to ensure DOM is ready
        const timeoutId = setTimeout(initSwiper, 200)

        // Cleanup function
        return () => {
            clearTimeout(timeoutId)
        }
    }, [swiperLoaded, combinedSlides.length, isClient, combinedSlides, videoId, setupBitmovin, playVideo, pauseVideo, setupActiveSliceLikeDislike, updateMeta]) // Only essential dependencies

    const handleBack = () => {
        if (onClose) onClose()
    }

    return (
        <>
            <Helmet>
                <link rel='stylesheet' href='https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css' />
                <link rel='stylesheet' href='https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css' />
                {/* <link rel='stylesheet' href='https://cdn.bitmovin.com/player/web/8/bitmovinplayer-ui.css' /> */}

                {/* Google Ad Manager (GPT) Scripts */}
                {/* <script async src='https://securepubads.g.doubleclick.net/tag/js/gpt.js'></script>
                <script
                    dangerouslySetInnerHTML={{
                        __html: `
                            window.googletag = window.googletag || {cmd: []};
                            googletag.cmd.push(function() {
                                googletag.pubads().collapseEmptyDivs();
                                googletag.enableServices();
                            });

                            function loadAd(adId) {
                                window.definedAdSlots = window.definedAdSlots || new Set();
                                googletag.cmd.push(function() {
                                    if (!document.getElementById(adId) || document.getElementById(adId).hasAttribute('data-loaded')) {
                                      return;
                                    }
                                    if (!window.definedAdSlots.has(adId)) {
                                        googletag.defineSlot('/1009103/BUTV3_VP_Banner', [
                                          [300, 600],
                                          [320, 480]
                                        ], adId).addService(googletag.pubads());
                                        window.definedAdSlots.add(adId);
                                    }
                                    document.getElementById(adId).setAttribute('data-loaded', 'true');
                                    googletag.display(adId);
                                    googletag.pubads().refresh([
                                      googletag.pubads().getSlots().find(slot => slot.getSlotElementId() === adId)
                                    ]);
                                });
                            }
                        `,
                    }}
                /> */}
            </Helmet>

            <div className='vertical-video-page'>
                {/* Back Button */}
                <div className='logo-back'>
                    <button onClick={handleBack} className='back-btn' tabIndex={0}>
                        <FaArrowLeft size={18} />
                    </button>
                    {/* <img src='/images/logo_butv3.png' className='logo' alt='logo_butv3' /> */}
                </div>

                {/* Swiper Container - HTML structure like PHP sample */}
                <div key={`swiper-${combinedSlides.length}`} className={`swiper mySwiper`}>
                    <div className='swiper-wrapper' id='swiper-wrapper'></div>
                    <div className='swiper-navigation'>
                        <div className='swiper-button-prev'></div>
                        <div className='swiper-button-next'></div>
                    </div>
                </div>

                {/* Global Mute/Unmute Button */}
                {currentSlideData?.type === 'video' && (
                    <button onClick={toggleGlobalMute} className='unmute-btn'>
                        {!globalMuteState.current ? <FaVolumeHigh size={20} /> : <FaVolumeXmark size={20} />}
                    </button>
                )}

                {/* Share Modal */}
                {shareModalOpen && (
                    <div className='share-modal'>
                        <div className='modal-content'>
                            <div className='modal-header'>
                                <h1 className='modal-title'>Kongsi Sekarang</h1>
                                <button onClick={() => setShareModalOpen(false)} className='modal-close' aria-label='Close'>
                                    <i className='fa-solid fa-xmark'></i>
                                </button>
                            </div>
                            <div className='modal-body'>
                                <div className='share-icons'>
                                    <button onClick={() => shareOnSocial('facebook')} className='share-icon share-fb'>
                                        <FaFacebook />
                                    </button>
                                    <button onClick={() => shareOnSocial('twitter')} className='share-icon share-tw'>
                                        <FaXTwitter />
                                    </button>
                                    <button onClick={() => shareOnSocial('whatsapp')} className='share-icon share-ws'>
                                        <FaWhatsapp />
                                    </button>
                                </div>
                                <div className='share-copy'>
                                    <i className='fas fa-link' style={{ color: '#666', fontSize: '1rem' }}></i>
                                    <input type='text' value={isClient ? window.location.href : ''} readOnly />
                                    <button onClick={copyToClipboard}>Copy</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    )
}

export default VerticalVideoPlayer